<script lang="ts">
  import { onMount, tick, createEventDispatcher } from 'svelte';
  import { toast } from '../../framework/js/toast.js';
  import { api } from '../../framework/js/api.js';
  import { auth } from '../../framework/js/auth.js';
  import { spinner } from '../../framework/js/spinner.js';
  import { loadStripeScript } from '../../framework/js/stripe-loader.js';
  import Modal from '../../framework/components/Modal.svelte';

  export let showModal = false;

  const dispatch = createEventDispatcher();

  // Steps: 1 = Team Details, 2 = Select Plan, 3 = Payment
  let step = 1;
  let teamName = '';
  let plans: any[] = [];
  let selectedPlan: any = null;
  let billingInterval: 'monthly' | 'annual' = 'monthly';
  let isProcessing = false;
  let isLoadingPlans = false;
  let modalWidth = 560;

  // Stripe / Payment
  let stripe: any;
  let elements: any;
  let cardNumber: any;
  let cardExpiry: any;
  let cardCvc: any;
  let cardholderName = '';
  let savedCards: any[] = [];
  let selectedCardId: string | null = null;
  let useNewCard = false;
  let isLoadingCards = false;

  const stripePublishableKey = typeof import.meta.env.VITE_STRIPE_KEY === 'string' ? (import.meta.env.VITE_STRIPE_KEY || '').trim() : '';

  // Reset when modal opens/closes
  let plansLoaded = false;
  $: if (showModal) {
    step = 1;
    teamName = '';
    selectedPlan = null;
    billingInterval = 'monthly';
    cardholderName = '';
    selectedCardId = null;
    useNewCard = false;
    isProcessing = false;
    if (!plansLoaded) loadPlans();
  }

  // Load Stripe when entering step 3
  $: if (step === 3 && stripePublishableKey && !stripe) {
    loadStripeScript().then((StripeFn) => {
      if (StripeFn) {
        stripe = StripeFn(stripePublishableKey);
        elements = stripe.elements();
      }
    });
  }

  $: if (step === 3) {
    loadSavedCards();
  }

  async function loadPlans() {
    isLoadingPlans = true;
    const res = await api.getPaymentPlans() as any;
    if (res.success) {
      plans = (res.data?.plans || []).filter((p: any) => !p.is_free_trial);
    plansLoaded = true;
    }
    isLoadingPlans = false;
  }

  async function loadSavedCards() {
    if (isLoadingCards) return;
    isLoadingCards = true;
    try {
      const response = await api.getUserCards(($auth as any).user.uuid) as any;
      if (response?.success) {
        savedCards = response.data || [];
        if (savedCards.length > 0) {
          selectedCardId = savedCards[0].id;
          useNewCard = false;
        } else {
          useNewCard = true;
        }
      } else {
        savedCards = [];
        useNewCard = true;
      }
    } catch {
      savedCards = [];
      useNewCard = true;
    } finally {
      isLoadingCards = false;
      if (useNewCard) {
        tick().then(() => setupCardElements());
      }
    }
  }

  function handleUseNewCard() {
    useNewCard = true;
    selectedCardId = null;
    tick().then(() => setupCardElements());
  }

  function setupCardElements() {
    if (cardNumber) { cardNumber.destroy(); cardNumber = null; }
    if (cardExpiry) { cardExpiry.destroy(); cardExpiry = null; }
    if (cardCvc) { cardCvc.destroy(); cardCvc = null; }

    const style = {
      base: {
        color: '#32325d',
        fontFamily: '"Poppins", "Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': { color: '#aab7c4' },
      },
      invalid: { color: '#fa755a', iconColor: '#fa755a' },
    };

    if (!stripe || !elements) return;

    const numberEl = document.getElementById('create-team-card-number');
    const expiryEl = document.getElementById('create-team-card-expiry');
    const cvcEl = document.getElementById('create-team-card-cvc');

    if (numberEl && expiryEl && cvcEl) {
      cardNumber = elements.create('cardNumber', { style });
      cardNumber.mount('#create-team-card-number');
      cardExpiry = elements.create('cardExpiry', { style });
      cardExpiry.mount('#create-team-card-expiry');
      cardCvc = elements.create('cardCvc', { style });
      cardCvc.mount('#create-team-card-cvc');
    }
  }

  function handleClose() {
    showModal = false;
  }

  function goToStep2() {
    if (!teamName.trim()) {
      toast.showNotification(false, 'Please enter a team name');
      return;
    }
    step = 2;
  }

  function goToStep3() {
    if (!selectedPlan) {
      toast.showNotification(false, 'Please select a plan');
      return;
    }
    step = 3;
  }

  function formatPrice(price: number): string {
    return new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  }

  function getDisplayPrice(plan: any): string {
    if (plan.included_briefs === 0) return formatPrice(plan.price);
    if (billingInterval === 'annual' && plan.annual_price) {
      return formatPrice(Math.round(parseFloat(plan.annual_price) / 12));
    }
    return formatPrice(plan.price);
  }

  function getPriceSuffix(plan: any): string {
    return plan.included_briefs === 0 ? '/brief' : '/mo';
  }

  function getCardBrandIcon(brand: string): string {
    const brandIcons: Record<string, string> = {
      'visa': 'cc visa', 'mastercard': 'cc mastercard', 'amex': 'cc amex',
      'discover': 'cc discover', 'diners': 'cc diners club', 'jcb': 'cc jcb',
    };
    return brandIcons[brand?.toLowerCase()] || 'credit card';
  }

  function capitalizeFirst(str: string): string {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
  }

  function getChargeAmount(): string {
    if (!selectedPlan) return '';
    if (selectedPlan.included_briefs === 0) return 'No upfront charge — pay per brief';
    if (billingInterval === 'annual' && selectedPlan.annual_price) {
      return formatPrice(selectedPlan.annual_price) + ' billed annually';
    }
    return formatPrice(selectedPlan.price) + ' billed monthly';
  }

  function isPAYG(): boolean {
    return selectedPlan && selectedPlan.included_briefs === 0;
  }

  async function handleSubmit() {
    let paymentMethodId: string | null = null;

    // PAYG plans don't require payment upfront
    if (!isPAYG()) {
      if (useNewCard) {
        if (!stripe) {
          toast.showNotification(false, 'Payment is not configured.');
          return;
        }
        if (!cardholderName.trim()) {
          toast.showNotification(false, 'Please enter the cardholder name');
          return;
        }

        isProcessing = true;
        try {
          const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardNumber,
            billing_details: { name: cardholderName },
          });
          if (error) {
            toast.showNotification(false, error.message);
            isProcessing = false;
            return;
          }
          paymentMethodId = paymentMethod.id;
        } catch {
          toast.showNotification(false, 'Error processing card');
          isProcessing = false;
          return;
        }
      } else {
        if (!selectedCardId) {
          toast.showNotification(false, 'Please select a payment method');
          return;
        }
        paymentMethodId = selectedCardId;
      }
    }

    isProcessing = true;
    spinner.show();

    try {
      // Step 1: Create the team
      const createRes = await api.createTeam(teamName.trim()) as any;
      if (!createRes.success) {
        toast.showNotification(false, createRes.message || 'Failed to create team');
        return;
      }

      const newTeamUuid = createRes.data.team.uuid;

      // Step 2: Assign plan to the new team
      const planRes = await api.assignTeamPlan(
        newTeamUuid,
        selectedPlan.uuid,
        billingInterval,
        paymentMethodId ?? ''
      ) as any;

      if (!planRes.success) {
        toast.showNotification(false, planRes.message || 'Team created but plan assignment failed. You can assign a plan later.');
      } else {
        toast.showNotification(true, `Team "${teamName.trim()}" created successfully!`);
      }

      // Switch to the new team
      auth.update((state: any) => {
        state.user.active_team_uuid = newTeamUuid;
        return state;
      });

      dispatch('created', { teamUuid: newTeamUuid });
      handleClose();
    } catch {
      toast.showNotification(false, 'Error creating team');
    } finally {
      spinner.hide();
      isProcessing = false;
    }
  }

  function getHeaderText(): string {
    if (step === 1) return 'Create New Team';
    if (step === 2) return 'Select a Plan';
    return 'Payment Details';
  }
</script>

<Modal bind:showModal bind:modalWidth>
  <div slot="header">
    <div class="modal-header-content">
      <span>{getHeaderText()}</span>
      <div class="step-indicator">
        <span class="step-dot" class:active={step >= 1} class:done={step > 1}></span>
        <span class="step-line" class:done={step > 1}></span>
        <span class="step-dot" class:active={step >= 2} class:done={step > 2}></span>
        <span class="step-line" class:done={step > 2}></span>
        <span class="step-dot" class:active={step >= 3}></span>
      </div>
    </div>
  </div>
  <div slot="body">
    {#if step === 1}
      <!-- Step 1: Team Details -->
      <div class="step-content">
        <div class="info-banner">
          <i class="info circle icon"></i>
          <span>You will be the <strong>owner</strong> of this team. A paid plan is required for additional teams.</span>
        </div>

        <div class="form-field">
          <label for="create-team-name" class="field-label">Team Name</label>
          <input
            id="create-team-name"
            type="text"
            class="field-input"
            bind:value={teamName}
            placeholder="e.g. Marketing Team"
            on:keydown={(e) => e.key === 'Enter' && goToStep2()}
          />
        </div>
      </div>

    {:else if step === 2}
      <!-- Step 2: Plan Selection -->
      <div class="step-content">
        <div class="billing-toggle">
          <button
            class="toggle-btn"
            class:active={billingInterval === 'monthly'}
            on:click={() => billingInterval = 'monthly'}
          >Monthly</button>
          <button
            class="toggle-btn"
            class:active={billingInterval === 'annual'}
            on:click={() => billingInterval = 'annual'}
          >Annual <span class="save-badge">Save 10%</span></button>
        </div>

        {#if isLoadingPlans}
          <div class="loading-state">
            <i class="spinner loading icon"></i>
            <span>Loading plans...</span>
          </div>
        {:else}
          <div class="plan-grid">
            {#each plans as plan}
              <button
                class="plan-card"
                class:selected={selectedPlan?.uuid === plan.uuid}
                on:click={() => selectedPlan = plan}
              >
                <div class="plan-card-header">
                  <h4 class="plan-card-name">{plan.name}</h4>
                  <div class="plan-card-price">
                    <span class="plan-price-amount">{getDisplayPrice(plan)}</span>
                    <span class="plan-price-suffix">{getPriceSuffix(plan)}</span>
                  </div>
                </div>
                <div class="plan-card-features">
                  {#if plan.included_briefs > 0}
                    <span class="plan-feature">{plan.included_briefs} briefs/mo</span>
                  {:else}
                    <span class="plan-feature">Pay per brief</span>
                  {/if}
                  <span class="plan-feature">{plan.max_team_seats ? `${plan.max_team_seats} seats` : 'Unlimited seats'}</span>
                </div>
                {#if selectedPlan?.uuid === plan.uuid}
                  <div class="selected-check"><i class="check circle icon"></i></div>
                {/if}
              </button>
            {/each}
          </div>
        {/if}
      </div>

    {:else if step === 3}
      <!-- Step 3: Payment -->
      <div class="step-content">
        <!-- Summary -->
        <div class="order-summary">
          <div class="summary-row">
            <span class="summary-label">Team</span>
            <span class="summary-value">{teamName}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Plan</span>
            <span class="summary-value">{selectedPlan?.name}</span>
          </div>
          <div class="summary-row highlight">
            <span class="summary-label">Billing</span>
            <span class="summary-value">{getChargeAmount()}</span>
          </div>
        </div>

        {#if !isPAYG()}
          <div class="divider"></div>

          <div class="payment-section">
            <h4 class="section-title">Payment Method</h4>

            {#if isLoadingCards}
              <div class="cards-loading">
                <i class="spinner loading icon"></i>
                <span>Loading payment methods...</span>
              </div>
            {:else}
              {#if savedCards.length > 0}
                <div class="payment-options">
                  {#each savedCards as card}
                    <label class="payment-option" class:selected={!useNewCard && selectedCardId === card.id}>
                      <input
                        type="radio"
                        name="create-team-payment"
                        value={card.id}
                        checked={!useNewCard && selectedCardId === card.id}
                        on:change={() => { selectedCardId = card.id; useNewCard = false; }}
                      />
                      <div class="option-content">
                        <i class="{getCardBrandIcon(card.brand)} icon card-brand-icon"></i>
                        <span class="card-info">{capitalizeFirst(card.brand)} •••• {card.last4}</span>
                      </div>
                    </label>
                  {/each}
                  <label class="payment-option" class:selected={useNewCard}>
                    <input
                      type="radio"
                      name="create-team-payment"
                      value="new"
                      checked={useNewCard}
                      on:change={handleUseNewCard}
                    />
                    <div class="option-content">
                      <i class="plus icon"></i>
                      <span>Add new card</span>
                    </div>
                  </label>
                </div>
              {/if}

              {#if useNewCard}
                <div class="new-card-form" class:has-saved-cards={savedCards.length > 0}>
                  <div class="form-field">
                    <label for="create-team-cardholder-name" class="field-label">Name on Card</label>
                    <input
                      id="create-team-cardholder-name"
                      type="text"
                      class="field-input"
                      bind:value={cardholderName}
                      placeholder="John Smith"
                    />
                  </div>
                  <div class="form-field">
                    <span class="field-label">Card Number</span>
                    <div id="create-team-card-number" class="stripe-input"></div>
                  </div>
                  <div class="form-row">
                    <div class="form-field half">
                      <span class="field-label">Expiry Date</span>
                      <div id="create-team-card-expiry" class="stripe-input"></div>
                    </div>
                    <div class="form-field half">
                      <span class="field-label">CVC</span>
                      <div id="create-team-card-cvc" class="stripe-input"></div>
                    </div>
                  </div>
                </div>
              {/if}
            {/if}
          </div>
        {:else}
          <div class="info-banner" style="margin-top: 1rem;">
            <i class="info circle icon"></i>
            <span>No upfront payment needed for Pay-As-You-Go. You'll be charged per brief.</span>
          </div>
        {/if}
      </div>
    {/if}
  </div>
  <div slot="footer">
    <div class="modal-actions">
      {#if step > 1}
        <button class="back-button" on:click={() => step--} disabled={isProcessing}>
          Back
        </button>
      {/if}
      <button class="cancel-button" on:click={handleClose} disabled={isProcessing}>
        Cancel
      </button>
      {#if step === 1}
        <button class="next-button" on:click={goToStep2} disabled={!teamName.trim()}>
          Next
        </button>
      {:else if step === 2}
        <button class="next-button" on:click={goToStep3} disabled={!selectedPlan}>
          Next
        </button>
      {:else}
        <button
          class="submit-button"
          on:click={handleSubmit}
          disabled={isProcessing || (!isPAYG() && isLoadingCards)}
        >
          {#if isProcessing}
            <i class="spinner loading icon"></i>
          {/if}
          Create Team & Subscribe
        </button>
      {/if}
    </div>
  </div>
</Modal>

<style>
  .modal-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .step-indicator {
    display: flex;
    align-items: center;
    gap: 0;
  }

  .step-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--border-color);
    transition: background 0.3s;
  }

  .step-dot.active {
    background: var(--primary);
  }

  .step-dot.done {
    background: #45c496;
  }

  .step-line {
    width: 24px;
    height: 2px;
    background: var(--border-color);
    transition: background 0.3s;
  }

  .step-line.done {
    background: #45c496;
  }

  .step-content {
    min-height: 150px;
  }

  .info-banner {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    background: #f0f7ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    font-size: 0.85rem;
    color: var(--grey);
    margin-bottom: 1.25rem;
    line-height: 1.5;
  }

  .info-banner i {
    color: var(--primary);
    margin: 0;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .field-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--grey);
  }

  .field-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 1rem;
    font-family: inherit;
    transition: border-color 0.2s, box-shadow 0.2s;
    background: var(--white);
    color: var(--grey);
    box-sizing: border-box;
  }

  .field-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  /* Step 2: Plan selection */
  .billing-toggle {
    display: flex;
    background: var(--greyish, #f0f0f0);
    border-radius: 8px;
    padding: 3px;
    margin-bottom: 1.25rem;
  }

  .toggle-btn {
    flex: 1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    background: transparent;
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--grey-secondary);
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
  }

  .toggle-btn.active {
    background: var(--white);
    color: var(--grey);
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .save-badge {
    font-size: 0.7rem;
    background: #dcfce7;
    color: #16a34a;
    padding: 1px 5px;
    border-radius: 4px;
    font-weight: 600;
  }

  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 2rem;
    color: var(--grey-secondary);
  }

  .plan-grid {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .plan-card {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 1rem 1.25rem;
    border: 2px solid var(--border-color);
    border-radius: 10px;
    background: var(--white);
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
    text-align: left;
  }

  .plan-card:hover {
    border-color: var(--primary);
  }

  .plan-card.selected {
    border-color: var(--primary);
    background: rgba(5, 108, 242, 0.03);
  }

  .plan-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.35rem;
  }

  .plan-card-name {
    font-size: 1rem;
    font-weight: 600;
    color: var(--grey);
    margin: 0;
  }

  .plan-card-price {
    display: flex;
    align-items: baseline;
    gap: 0.15rem;
  }

  .plan-price-amount {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--primary);
  }

  .plan-price-suffix {
    font-size: 0.75rem;
    color: var(--grey-secondary);
  }

  .plan-card-features {
    display: flex;
    gap: 1rem;
  }

  .plan-feature {
    font-size: 0.8rem;
    color: var(--grey-secondary);
  }

  .selected-check {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }

  .selected-check i {
    color: var(--primary);
    font-size: 1.1rem;
    margin: 0;
  }

  /* Step 3: Payment */
  .order-summary {
    background: linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%);
    border-radius: 10px;
    padding: 1rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.875rem;
  }

  .summary-label {
    color: var(--grey-secondary);
  }

  .summary-value {
    font-weight: 500;
    color: var(--grey);
  }

  .summary-row.highlight {
    padding-top: 0.5rem;
    border-top: 1px solid var(--border-color);
    margin-top: 0.25rem;
  }

  .summary-row.highlight .summary-value {
    color: var(--primary);
    font-weight: 600;
  }

  .divider {
    height: 1px;
    background: var(--border-color);
    margin: 1.25rem 0;
  }

  .payment-section {
    margin-top: 0.5rem;
  }

  .section-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--grey);
    margin: 0 0 1rem 0;
  }

  .cards-loading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    color: var(--grey-secondary);
    font-size: 0.875rem;
  }

  .payment-options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .payment-option {
    display: flex;
    align-items: center;
    padding: 0.875rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
  }

  .payment-option:hover {
    border-color: var(--primary);
    background: #f8fafc;
  }

  .payment-option.selected {
    border-color: var(--primary);
    background: rgba(5, 108, 242, 0.05);
  }

  .payment-option input[type="radio"] {
    margin-right: 0.75rem;
    accent-color: var(--primary);
  }

  .option-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
  }

  .card-brand-icon {
    font-size: 1.5rem !important;
    color: var(--grey);
    margin: 0 !important;
  }

  .card-info {
    flex: 1;
    font-size: 0.9375rem;
    color: var(--grey);
  }

  .new-card-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .new-card-form.has-saved-cards {
    padding-top: 0.5rem;
    border-top: 1px solid var(--border-color);
    margin-top: 0.5rem;
  }

  .stripe-input {
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--white);
    transition: border-color 0.2s, box-shadow 0.2s;
    min-height: 44px;
  }

  .stripe-input:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  /* Footer actions */
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding-top: 0.5rem;
  }

  .cancel-button, .back-button {
    padding: 0.75rem 1.25rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--white);
    color: var(--grey);
    font-weight: 500;
    font-size: 0.9375rem;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
  }

  .cancel-button:hover:not(:disabled), .back-button:hover:not(:disabled) {
    background: var(--greyish);
    border-color: var(--grey-secondary);
  }

  .cancel-button:disabled, .back-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .back-button {
    margin-right: auto;
  }

  .next-button, .submit-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border: 0;
    border-radius: 6px;
    background: var(--primary);
    color: var(--white);
    font-weight: 500;
    font-size: 0.9375rem;
    cursor: pointer;
    transition: background 0.2s;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
  }

  .next-button:hover:not(:disabled), .submit-button:hover:not(:disabled) {
    background: var(--darkprimary);
  }

  .next-button:disabled, .submit-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .submit-button {
    background: #45c496;
  }

  .submit-button:hover:not(:disabled) {
    background: #3aaa82;
  }

  .submit-button i {
    margin: 0;
  }

  @media (max-width: 480px) {
    .form-row {
      grid-template-columns: 1fr;
    }
  }
</style>
